/**
 * Phase 2 (Feb 2026) — Campaign traffic-light tiles.
 *
 * §7.3 Frontend-V2 concept: split the campaign operational status from
 * its economic verdict. Two independent chips, never merged. Tile keeps
 * the tenant-facing plain-german status: "Läuft" / "Pausiert" /
 * "Wird angehalten" etc. + a separate business chip: "im Rahmen" /
 * "beobachten" / "nicht rentabel".
 *
 * Numbers come from GET /campaigns and /cockpit/summary (both existing).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { api } from "../../lib/api";
import EmptyState from "./EmptyState";
import { CircleDot, TrendingUp, TrendingDown, Activity } from "lucide-react";

const OPERATIONAL_LABEL = {
  active: { text: "Läuft", tone: "default", dot: "text-emerald-500" },
  scheduled: { text: "Bereit", tone: "outline", dot: "text-blue-500" },
  paused: { text: "Pausiert", tone: "secondary", dot: "text-amber-500" },
  stopping: { text: "Wird angehalten", tone: "secondary", dot: "text-amber-500" },
  stopped: { text: "Angehalten", tone: "outline", dot: "text-muted-foreground" },
  draft: { text: "Entwurf", tone: "outline", dot: "text-muted-foreground" },
  failed: { text: "Fehler", tone: "destructive", dot: "text-red-500" },
};

function economicVerdict(perf) {
  const roas = perf?.roas;
  const cpl = perf?.cpl_eur;
  if (roas == null && cpl == null) {
    return { text: "Zu wenig Daten", tone: "outline", icon: Activity };
  }
  if (roas != null) {
    if (roas >= 2) return { text: "Im Rahmen", tone: "default", icon: TrendingUp };
    if (roas >= 1) return { text: "Beobachten", tone: "secondary", icon: Activity };
    return { text: "Nicht rentabel", tone: "destructive", icon: TrendingDown };
  }
  if (cpl != null && cpl <= 30) return { text: "Im Rahmen", tone: "default", icon: TrendingUp };
  return { text: "Beobachten", tone: "secondary", icon: Activity };
}

export default function CampaignTrafficLightTiles() {
  const [campaigns, setCampaigns] = useState(null);

  useEffect(() => {
    api
      .get("/campaigns?limit=6")
      .then((r) => setCampaigns(r.data?.items || []))
      .catch(() => setCampaigns([]));
  }, []);

  if (campaigns == null) {
    return (
      <Card data-testid="campaign-tiles-loading">
        <CardContent className="py-8 text-sm text-muted-foreground text-center">
          Lade Kampagnen …
        </CardContent>
      </Card>
    );
  }
  if (campaigns.length === 0) {
    return (
      <EmptyState
        title="Noch keine Kampagne"
        description="Starte deine erste Kampagne mit dem Profi-Chat oder dem Kampagnen-Wizard."
        ctaLabel="Jetzt starten"
        ctaLink="/app/campaigns/new"
        testid="campaign-tiles-empty"
      />
    );
  }
  return (
    <Card data-testid="campaign-tiles">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          Deine Kampagnen
          <Link
            to="/app/campaigns"
            className="text-xs font-normal underline text-muted-foreground hover:text-foreground"
            data-testid="campaign-tiles-view-all"
          >
            Alle ansehen
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {campaigns.map((c) => {
          const op = OPERATIONAL_LABEL[c.publish_status || c.status] ||
            { text: c.status || "?", tone: "outline", dot: "text-muted-foreground" };
          const econ = economicVerdict(c.performance || {});
          const Econ = econ.icon;
          return (
            <Link
              key={c.id}
              to={`/app/campaigns/${c.id}`}
              data-testid={`campaign-tile-${c.id}`}
              className="rounded-md border p-3 hover:shadow-md transition-shadow bg-card"
            >
              <div className="flex items-center gap-2 mb-2">
                <CircleDot className={`w-3 h-3 ${op.dot}`} />
                <span className="font-medium text-sm truncate">{c.name}</span>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Badge variant={op.tone} data-testid={`campaign-tile-op-${c.id}`}>
                  Betrieb: {op.text}
                </Badge>
                <Badge variant={econ.tone} data-testid={`campaign-tile-econ-${c.id}`}>
                  <Econ className="w-3 h-3 mr-1" />
                  Wirtschaft: {econ.text}
                </Badge>
              </div>
              <div className="mt-2 text-[11px] text-muted-foreground">
                {(c.platforms || []).join(" · ") || "keine Kanäle"}
              </div>
            </Link>
          );
        })}
      </CardContent>
    </Card>
  );
}
